import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CookieService, CookieOptions } from 'ngx-cookie';
import { from, Observable, of, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ANALYTICS_TOKEN_COOKIE } from '../interceptors/analytics-auth.interceptor';
import { decryptWithSeed, encryptWithSeed } from './analytics-session-crypto';

interface AnalyticsLoginRequest {
  username: string;
  password: string;
}

interface AnalyticsLoginResponse {
  access_token: string;
  expires_at: string;
  token_type?: 'bearer';
}

/** Что лежит в sessionStorage. password — base64(AES-GCM ciphertext), не plaintext. */
interface PersistedCreds {
  username: string;
  encPassword: string;
}

const SESSION_KEY = 'analytics.creds.v1';
/**
 * Параметры адаптивного refresh — lead подбирается по TTL токена, а не
 * фиксированным окном. Если бэк выдаёт токен с TTL = 15 мин и lead тоже
 * = 15 мин, то `expires_at − lead` оказывается в прошлом, и таймер
 * вырождается в MIN_REFRESH_DELAY_MS — silent-login начинает дёргаться
 * каждые N секунд, бэк банит за rate-limit, и обновление перестаёт работать.
 */
const MIN_REFRESH_DELAY_MS = 30_000;
/** Lead не больше 5 минут — даже если TTL длинный, нет смысла refresh'ить за час. */
const MAX_REFRESH_LEAD_MS = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AnalyticsAuthService implements OnDestroy {
  private _refreshTimer: ReturnType<typeof setTimeout> | null = null;
  /** Промис активного refresh — чтобы параллельные 401 не запускали 5 silentLogin одновременно. */
  private _inflightRefresh: Promise<boolean> | null = null;

  constructor(
    private readonly _http: HttpClient,
    private readonly _cookies: CookieService,
    private readonly _zone: NgZone,
  ) {
    // На старте (F5 или прямой заход в /analytics) — если cookie токена нет,
    // но в sessionStorage лежат зашифрованные креды, пробуем silentLogin
    // ещё до первого API-запроса. Если не вышло — interceptor поймает 401.
    if (!this.hasToken() && this._readPersisted()) {
      this._refreshFromPersisted();
    }
  }

  ngOnDestroy(): void {
    this._clearRefreshTimer();
  }

  hasToken(): boolean {
    return !!this._cookies.get(ANALYTICS_TOKEN_COOKIE);
  }

  /**
   * Тихий логин в analytics. Никогда не бросает — если не получилось,
   * пользователь всё равно работает в основном приложении, а analytics-вкладка
   * просто покажет «нет доступа к аналитическому сервису».
   */
  silentLogin(form: AnalyticsLoginRequest): Observable<AnalyticsLoginResponse | null> {
    // Используем нативный fetch вместо HttpClient — обходим все Angular interceptors.
    // С HttpClient тело улетало без поля username (вероятно какой-то interceptor чистит body
    // при clone({headers})), переключение на fetch это починило. См. историю коммитов 762.
    return from(this._doLogin(form)).pipe(
      tap((res) => {
        if (res?.access_token) {
          const opts: CookieOptions = {};
          if (res.expires_at) opts.expires = new Date(res.expires_at);
          this._cookies.put(ANALYTICS_TOKEN_COOKIE, res.access_token, opts);
          // Запомнили креды (шифрованно) + поставили будильник на pre-expire refresh.
          this._persist(form);
          this._scheduleRefresh(res.expires_at);
        }
      }),
      catchError(() => of(null)),
    );
  }

  /**
   * Используется интерсептором при 401. Promise, не Observable, чтобы
   * coalesce-ить параллельные вызовы через _inflightRefresh.
   * Возвращает true если после refresh в cookie появился свежий токен.
   */
  refreshFromPersisted(): Promise<boolean> {
    if (this._inflightRefresh) return this._inflightRefresh;
    this._inflightRefresh = this._refreshFromPersisted().finally(() => {
      this._inflightRefresh = null;
    });
    return this._inflightRefresh;
  }

  clearToken(): void {
    this._cookies.remove(ANALYTICS_TOKEN_COOKIE);
    this._clearPersisted();
    this._clearRefreshTimer();
  }

  // ====== internals ======

  private async _doLogin(form: AnalyticsLoginRequest): Promise<AnalyticsLoginResponse | null> {
    try {
      const res = await fetch(`${environment.analyticsApiUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) return null;
      return (await res.json()) as AnalyticsLoginResponse;
    } catch {
      return null;
    }
  }

  private async _refreshFromPersisted(): Promise<boolean> {
    const persisted = this._readPersisted();
    if (!persisted) return false;
    const seed = this._cookies.get('token');
    if (!seed) return false;
    const password = await decryptWithSeed(persisted.encPassword, seed);
    if (!password) {
      // Main-токен сменился → не сможем продлить. Чистим протухшее.
      this._clearPersisted();
      return false;
    }
    const res = await this._doLogin({ username: persisted.username, password });
    if (!res?.access_token) return false;
    const opts: CookieOptions = {};
    if (res.expires_at) opts.expires = new Date(res.expires_at);
    this._cookies.put(ANALYTICS_TOKEN_COOKIE, res.access_token, opts);
    this._scheduleRefresh(res.expires_at);
    return true;
  }

  private _persist(form: AnalyticsLoginRequest): void {
    const seed = this._cookies.get('token');
    if (!seed) return; // нет main-токена → нечем шифровать
    encryptWithSeed(form.password, seed)
      .then((encPassword) => {
        try {
          sessionStorage.setItem(
            SESSION_KEY,
            JSON.stringify({ username: form.username, encPassword } as PersistedCreds),
          );
        } catch {
          /* sessionStorage недоступен (private mode / quota) — терпим */
        }
      })
      .catch(() => {
        /* WebCrypto не сработал — пропускаем persist */
      });
  }

  private _readPersisted(): PersistedCreds | null {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as PersistedCreds;
      return parsed?.username && parsed?.encPassword ? parsed : null;
    } catch {
      return null;
    }
  }

  private _clearPersisted(): void {
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* ignore */
    }
  }

  private _scheduleRefresh(expiresAt?: string): void {
    this._clearRefreshTimer();
    if (!expiresAt) return;
    const exp = new Date(expiresAt).getTime();
    if (!Number.isFinite(exp)) return;
    const remaining = exp - Date.now();
    // Адаптивный lead: треть оставшейся жизни, но не больше 5 минут.
    // Так для TTL = 15 мин получим refresh через 10 мин (за 5 мин до конца);
    // для TTL = 60 мин — через 55 мин (за 5 мин до конца).
    // Минимум MIN_REFRESH_DELAY_MS, чтобы не закольцеваться при кривом expires_at.
    const lead = Math.max(MIN_REFRESH_DELAY_MS, Math.min(remaining / 3, MAX_REFRESH_LEAD_MS));
    const delay = Math.max(MIN_REFRESH_DELAY_MS, remaining - lead);
    // Запускаем таймер вне Angular zone, чтобы не дёргать CD каждые N часов.
    this._zone.runOutsideAngular(() => {
      this._refreshTimer = setTimeout(() => {
        this.refreshFromPersisted();
      }, delay);
    });
  }

  private _clearRefreshTimer(): void {
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer);
      this._refreshTimer = null;
    }
  }
}
